/**
 * characterData.js
 * Provides character data and metadata for the application.
 *
 * Per-character optional overrides recognised by the renderer:
 *   - modelFormat:        "glb" | "fbx"  (default "fbx")
 *   - modelScale:         number      manual escape hatch for the GLB
 *                                     auto-fit pipeline in
 *                                     ModelViewer.loadModel().
 *   - modelRotationY:     number      Y-rotation in radians applied in
 *                                     ModelViewer.applyPose() so the
 *                                     character faces the camera. Default
 *                                     0 (i.e. -Z forward). Use this for
 *                                     assets whose authored forward axis
 *                                     is not -Z.
 *   - framingYOffset:     number      Vertical nudge in cm applied to
 *                                     the eye-framing camera target.
 *                                     Positive shifts the framed
 *                                     portrait UP in the viewport,
 *                                     negative shifts it DOWN. Default 0.
 *   - framingHeadroomMin: number      Minimum breathing room in cm
 *                                     above the head and below the
 *                                     chin in the framed bounding
 *                                     box. Larger => looser frame
 *                                     (more empty space around the
 *                                     head). Default 10.
 */

export const characterData = {
    "bc5d31a8-047b-4f72-8f16-fea7a365ca69": {
        name: "Richard Feynman",
        origin: "Queens, New York, United States",
        expertise: "Theoretical Physics, Quantum Mechanics, Particle Physics",
        description:
            "Nobel Prize-winning physicist known for his work in quantum electrodynamics and his unique teaching methods.",
        imageUrl: "/static/images/RichardFeynman.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FRichard%20Feynman%20Greeting.mp3?alt=media&token=571eee3c-77a7-4e63-a88a-cf58ad7ed4c5",
        ],
        quotes: [
            "I would rather have questions that can't be answered than answers that can't be questioned.",
            "Study hard what interests you the most in the most undisciplined, irreverent and original manner possible.",
            "The first principle is that you must not fool yourself - and you are the easiest person to fool.",
            "I learned very early the difference between knowing the name of something and knowing something.",
            "What I cannot create, I do not understand.",
        ],
        modelFormat: "glb",
    },

    "0c6099ed-c0db-4dd7-bc46-1d720324db5a": {
        name: "Michael Faraday",
        origin: "Newington Butts, England",
        expertise:
            "Electromagnetism, Electricity, Experimental Physics, Electrochemistry",
        description:
            "Pioneering experimental scientist whose discovery of electromagnetic induction laid the foundation for electric power and modern electrical engineering.",
        imageUrl: "/static/images/Michael.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FMichael%20Faraday%20Greeting.mp3?alt=media&token=dce5e964-5a10-4995-b5f6-75fae227ab60",
        ],
        quotes: [
            "Nothing is too wonderful to be true, if it be consistent with the laws of nature.",
            "Work. Finish. Publish.",
            "The important thing is to know how to take all things quietly.",
            "Speculations? I have none. I am resting on certainties.",
            "But still try, for who knows what is possible?",
        ],
    },

    "01db85dc-81e0-4433-9440-a54ce4976178": {
        name: "Erwin Schrodinger",
        origin: "Vienna, Austria",
        expertise:
            "Quantum Mechanics, Wave Mechanics, Theoretical Physics, Quantum Theory",
        description:
            "Pioneering physicist who formulated the Schrödinger equation and laid the foundation of wave mechanics in quantum theory.",
        imageUrl: "/static/images/Erwin.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FErwin%20Schrodinger%20Greeting.mp3?alt=media&token=7cfcf673-8d0b-4d54-b101-50f0cf771c21",
        ],
        quotes: [
            "The task is not so much to see what no one has yet seen, but to think what nobody has yet thought about that which everybody sees.",
            "If you cannot — in the long run — tell everyone what you have been doing, your doing has been worthless.",
            "Quantum mechanics is very imposing. But an inner voice tells me that it is not yet the real thing.",
            "Consciousness cannot be accounted for in physical terms.",
            "We do not belong to this material world that science constructs for us.",
        ],
    },

    "4f50a698-6034-49c1-b9d4-221f59bf2378": {
        name: "Frida Kahlo",
        origin: "Coyoacán, Mexico City, Mexico",
        expertise:
            "Painting, Self-Portraiture, Mexican Folk Art, Symbolism",
        description:
            "Iconic Mexican painter renowned for her vivid, deeply personal self-portraits that fused folk art, surrealism, and raw emotional honesty about pain, identity, and the female experience.",
        imageUrl: "/static/images/Frida.png",
        greetings: [],
        quotes: [
            "I paint myself because I am so often alone and because I am the subject I know best.",
            "Feet, what do I need them for if I have wings to fly?",
            "At the end of the day, we can endure much more than we think we can.",
            "I paint flowers so they will not die.",
            "Nothing is worth more than laughter. It is strength to laugh and to abandon oneself, to be light.",
        ],
        modelFormat: "glb",
    },

    "91d5cec5-1c21-423f-bbb4-bceb0f77a6c9": {
        name: "Nostradamus",
        origin: "Saint-Rémy-de-Provence, France",
        expertise:
            "Astrology, Prophecy, Medicine, Renaissance Esotericism",
        description:
            "French astrologer, physician, and seer famous for Les Prophéties, his collection of cryptic quatrains that have been read for centuries as predictions of future events.",
        imageUrl: "/static/images/Nostradamus.png",
        greetings: [],
        quotes: [
            "The year 1999, seventh month, from the sky will come a great King of Terror.",
            "Where all is well, all is well, but in the middle is the doctor.",
            "There is no greater darkness than ignorance.",
            "Wisdom is born from suffering, and suffering teaches the wise to look beyond the hour.",
            "The future I do not invent; I merely read what the stars are willing to whisper.",
        ],
        modelFormat: "glb",
    },

    "88c314c4-5890-4e55-9418-8b46a376ef81a": {
        name: "Friedrich Nietzsche",
        origin: "Röcken, Prussia (now Germany)",
        expertise:
            "Moral Philosophy, Existential Thought, Cultural Critique, Psychology of Power",
        description:
            "Influential German philosopher who challenged traditional morality, proclaimed the 'death of God,' and introduced concepts such as the Übermensch and the Will to Power, shaping modern existential and postmodern thought.",
        imageUrl: "/static/images/Nietzsche.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FNietzcheGreeting.mp3?alt=media&token=8be021db-e6e2-4085-aab2-bc243b49f62c",
        ],
        quotes: [
            "He who has a why to live can bear almost any how.",
            "That which does not kill us makes us stronger.",
            "There are no facts, only interpretations.",
            "Become who you are.",
            "He who fights with monsters should be careful lest he thereby become a monster.",
        ],
        modelFormat: "glb",
    },

    "2db1d398-0161-46cd-8aff-53008d29a9dc": {
        name: "Claude Shannon",
        origin: "Petoskey, Michigan, United States",
        expertise:
            "Information Theory, Digital Communication, Cryptography, Computer Science",
        description:
            "Founder of information theory whose work established the mathematical foundation of digital communication and modern computing.",
        imageUrl: "/static/images/Claude.png",
        // Without this, getModelFormat() defaults to "fbx" and the GLB
        // never loads (FBXLoader on glTF bytes) — the Marcus Aurelius bug.
        modelFormat: "glb",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FClaude%20%20Shannon%20Greeting.mp3?alt=media&token=b772f447-36e9-4219-ae34-f7934851f6ea",
        ],
        quotes: [
            "Information is the resolution of uncertainty.",
            "The most important thing is to keep questioning.",
            "I visualize a time when we will be to robots what dogs are to humans.",
            "My greatest concern was what to call it. I thought of calling it 'information,' but the word was overly used.",
            "I always pursued what was fun.",
        ],
    },

    "c2cc71ce-89e7-4c94-9441-eec86ec67a60": {
        name: "Napoleon Bonaparte",
        origin: "Ajaccio, Corsica",
        expertise:
            "Military Strategy, Leadership, Political Reform, Empire Building",
        description:
            "Legendary French military commander and emperor whose strategic brilliance reshaped Europe and whose legal reforms influenced modern governance.",
        imageUrl: "/static/images/Napoleon.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FNapoleon%20Bonaparte%20Greeting.mp3?alt=media&token=c16ef2ce-6adc-4035-aeda-4b46b700b8de",
        ],
        quotes: [
            "Impossible is a word to be found only in the dictionary of fools.",
            "A leader is a dealer in hope.",
            "Victory belongs to the most persevering.",
            "Take time to deliberate, but when the time for action comes, stop thinking and go in.",
            "Ability is nothing without opportunity.",
        ],
    },

    "1a35136c-42d8-46c7-a189-c4b32da87832": {
        name: "John Neumann",
        origin: "Budapest, Hungary",
        expertise:
            "Mathematics, Computer Science, Game Theory, Artificial Intelligence",
        description:
            "Visionary polymath who shaped modern computing, game theory, and the architecture underlying today’s artificial intelligence systems.",
        imageUrl: "/static/images/JohnNeumann.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FJohn%20Neumann%20Greeting.mp3?alt=media&token=10733293-4d77-49a1-9931-be2062ca04ce",
        ],
        quotes: [
            "In mathematics, you don’t understand things. You just get used to them.",
            "Anyone who considers arithmetical methods of producing random digits is, of course, in a state of sin.",
            "If people do not believe that mathematics is simple, it is only because they do not realize how complicated life is.",
            "There’s no sense in being precise when you don’t even know what you’re talking about.",
            "With four parameters I can fit an elephant, and with five I can make him wiggle his trunk.",
        ],
    },

    "be0dcf0f-c72b-48f7-a462-6ba7560498d7": {
        name: "Alan Turing",
        origin: "London, England, United Kingdom",
        expertise:
            "Mathematics, Computer Science, Cryptography, Artificial Intelligence",
        description:
            "Pioneering mathematician and computer scientist, widely regarded as the father of modern computing and artificial intelligence.",
        imageUrl: "/static/images/Alan.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FAlanGreeting.mp3?alt=media&token=d6285b73-8748-43cb-98f1-3215f018b0e5",
        ],
        quotes: [
            "We can only see a short distance ahead, but we can see plenty there that needs to be done.",
            "Those who can imagine anything, can create the impossible.",
            "A computer would deserve to be called intelligent if it could deceive a human into believing that it was human.",
            "Sometimes it is the people no one imagines anything of who do the things that no one can imagine.",
            "Science is a differential equation. Religion is a boundary condition.",
        ],
        modelFormat: "glb",
    },

    "d537d61d-0313-421b-ae66-48a8d7222b3f": {
        name: "Nikola Tesla",
        origin: "Smiljan, Austrian Empire (modern-day Croatia)",
        expertise:
            "Electrical Engineering, Electromagnetism, Alternating Current, Wireless Power",
        description:
            "Visionary inventor and electrical engineer known for pioneering alternating current systems, groundbreaking experiments in electromagnetism, and his bold vision of wireless energy for humanity.",
        imageUrl: "/static/images/NikolaTesla.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FTesla%20Intro.mp3?alt=media&token=a51f9c55-bde7-41f2-a5fc-6ba5f20464b1",
        ],
        quotes: [
            "If you want to find the secrets of the universe, think in terms of energy, frequency, and vibration.",
            "The present is theirs; the future, for which I really worked, is mine.",
            "My brain is only a receiver, in the universe there is a core from which we obtain knowledge, strength, and inspiration.",
            "I do not care that they stole my idea. I care that they do not have any of their own.",
            "Let the future tell the truth, and evaluate each one according to his work and accomplishments.",
        ],
        modelFormat: "glb",
    },

    "16f5b6a1-45f6-4c01-a612-60d4e4ab2297": {
        name: "René Descartes",
        origin: "La Haye en Touraine, Kingdom of France",
        expertise:
            "Rationalism, Analytic Geometry, Philosophy of Mind, Scientific Method",
        description:
            "French philosopher and mathematician regarded as the father of modern philosophy and the founder of analytic geometry, known for establishing methodical doubt and the famous principle 'Cogito, ergo sum.'",
        imageUrl: "/static/images/ReneDescartes.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FRene%20Descartes%20Intro.mp3?alt=media&token=52753aa2-0598-4ba3-a902-59839054531d",
        ],
        quotes: [
            "Cogito, ergo sum. (I think, therefore I am.)",
            "It is not enough to have a good mind; the main thing is to use it well.",
            "To live without philosophizing is in truth the same as keeping the eyes closed without attempting to open them.",
            "If you would be a real seeker after truth, it is necessary that at least once in your life you doubt, as far as possible, all things.",
            "Divide each difficulty into as many parts as is feasible and necessary to resolve it.",
        ],
        modelFormat: "glb",
    },

    "5eb1ad8d-4c11-4174-b79a-c232991d015b": {
        name: "Oppenheimer",
        origin: "New York City, New York, United States",
        expertise: "Theoretical Physics, Quantum Mechanics, Nuclear Physics",
        description:
            "American theoretical physicist and scientific director of the Manhattan Project, best known for leading the development of the first atomic bomb and later reflecting deeply on the ethical responsibilities of science.",
        imageUrl: "/static/images/Oppenheimer.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FOppenhimer%20Intro.mp3?alt=media&token=d2725cc1-f9b5-4b07-beec-894aabe1c9db",
        ],
        quotes: [
            "Now I am become Death, the destroyer of worlds.",
            "The physicists have known sin; and this is a knowledge which they cannot lose.",
            "In some sort of crude sense which no vulgarity, no humor, no overstatement can quite extinguish, the physicists have known sin.",
            "There are children playing in the streets who could solve some of my top physics problems.",
            "We knew the world would not be the same.",
        ],
        //modelFormat: "glb",
    },

    "7021ffd6-51bb-41a8-920e-935899dcd631": {
        name: "Isaac Newton",
        origin: "Woolsthorpe, Lincolnshire, England",
        expertise: "Classical Mechanics, Mathematics, Optics, Astronomy",
        description:
            "Foundational scientist of the Scientific Revolution, best known for the laws of motion, universal gravitation, and the development of calculus.",
        imageUrl: "/static/images/Newton.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FNewtonIntro.mp3?alt=media&token=948b4f26-280c-427d-aa59-9eccad174fd6",
        ],
        quotes: [
            "If I have seen further it is by standing on the shoulders of giants.",
            "Truth is ever to be found in simplicity, and not in the multiplicity and confusion of things.",
            "What we know is a drop, what we don't know is an ocean.",
            "I can calculate the motion of heavenly bodies, but not the madness of people.",
            "To myself I seem to have been only like a boy playing on the seashore, whilst the great ocean of truth lay all undiscovered before me.",
        ],
        modelFormat: "glb",
    },

    "93ea7051-9722-443d-90fb-18a84c51894c": {
        name: "James Clerk Maxwell",
        origin: "Edinburgh, Scotland, United Kingdom",
        expertise:
            "Electromagnetism, Classical Physics, Statistical Mechanics, Optics",
        description:
            "Scottish physicist known for unifying electricity, magnetism, and light through Maxwell’s Equations — laying the foundation for modern physics and electrical engineering.",
        imageUrl: "/static/images/JamesClerk.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FJames%20Clerk%20Maxwell%20Greeting.mp3?alt=media&token=04527b63-04da-478b-9591-83af840696bc",
        ],
        quotes: [
            "The only laws of matter are those which our minds must fabricate, and the only laws of mind are fabricated for matter.",
            "Thoroughly conscious ignorance is the prelude to every real advance in science.",
            "In Science, the most important thing is to never stop questioning.",
            "Maxwell’s equations have changed the world; they will be remembered as long as the world stands.",
            "All the colors of the spectrum arise from the same light, divided and recombined.",
        ],
        modelFormat: "glb",
    },

    "b20dc26f-8924-4aee-9ef5-9b98c0367a34": {
        name: "Ernest Hemingway",
        origin: "Oak Park, Illinois, United States",
        expertise:
            "Modern Literature, Journalism, War Correspondence, Minimalist Writing Style",
        description:
            "Pulitzer and Nobel Prize–winning American author known for his bold minimalist prose, adventurous life, and influential works such as *The Old Man and the Sea*, *For Whom the Bell Tolls*, and *A Farewell to Arms*.",
        imageUrl: "/static/images/Hemingway.png",

        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FErnest%20Hemingway%20Greeting.mp3?alt=media&token=ce3d07a9-313b-4c4a-87de-7185c5d8b2e8",
        ],

        quotes: [
            "There is nothing noble in being superior to your fellow man; true nobility is being superior to your former self.",
            "The world breaks everyone, and afterward, some are strong at the broken places.",
            "Courage is grace under pressure.",
            "All you have to do is write one true sentence. Write the truest sentence that you know.",
            "Happiness in intelligent people is the rarest thing I know.",
        ],
        modelFormat: "glb",
    },

    "897eb00c-4f16-4f73-81d5-d1c91fad53f2": {
        name: "Charles Darwin",
        origin: "Shrewsbury, Shropshire, England",
        expertise: "Natural History, Evolutionary Biology, Geology",
        description:
            "Pioneering naturalist best known for formulating the theory of evolution by natural selection, reshaping modern biology and our understanding of life's diversity.",
        imageUrl: "/static/images/Darwin.png",

        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FCharles%20Darwin%20Greeting.mp3?alt=media&token=4d3dfffb-b0f3-42b5-9d49-c9f36f8297fb",
        ],

        quotes: [
            "It is not the strongest of the species that survives, nor the most intelligent, but the one most adaptable to change.",
            "I am not apt to follow blindly the lead of other men.",
            "A man who dares to waste one hour of time has not discovered the value of life.",
            "In the long history of humankind, those who learned to collaborate and improvise most effectively have prevailed.",
            "Ignorance more frequently begets confidence than does knowledge.",
        ],
        modelFormat: "glb",
    },

    "73f610bf-d9c9-4d9f-a026-a10086a66725": {
        name: "Marie Curie",
        origin: "Warsaw, Poland",
        expertise: "Physics, Chemistry, Radioactivity",
        description:
            "Pioneer in radioactivity research and the first person to win Nobel Prizes in two scientific fields.",
        imageUrl: "/static/images/MarieCurie.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FMarie%20Curie%20Greeting.mp3?alt=media&token=571eee3c-77a7-4e63-a88a-cf58ad7ed4c5",
        ],
        quotes: [
            "Nothing in life is to be feared, it is only to be understood. Now is the time to understand more, so that we may fear less.",
            "Be less curious about people and more curious about ideas.",
            "I was taught that the way of progress was neither swift nor easy.",
            "One never notices what has been done; one can only see what remains to be done.",
            "Life is not easy for any of us. But what of that? We must have perseverance and, above all, confidence in ourselves.",
        ],
        modelFormat: "glb",
    },
    "802bd089-d231-4c4e-830c-476199321af8": {
        name: "Niels Bohr",
        origin: "Copenhagen, Denmark",
        expertise: "Atomic Structure, Quantum Theory",
        description:
            "Pioneering physicist who made fundamental contributions to understanding atomic structure and quantum theory.",
        imageUrl: "/static/images/NielsBohr.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FNiels%20Bohr%20Greeting.mp3?alt=media&token=d55613ef-9396-4daf-8052-e287e85ae2df",
        ],
        quotes: [
            "Prediction is very difficult, especially if it's about the future.",
            "An expert is a man who has made all the mistakes which can be made, in a narrow field.",
            "The opposite of a correct statement is a false statement. But the opposite of a profound truth may well be another profound truth.",
            "How wonderful that we have met with a paradox. Now we have some hope of making progress.",
            "Everything we call real is made of things that cannot be regarded as real.",
        ],
        modelFormat: "glb",
    },
    "992e9007-b4f8-4711-80c2-884daf3d9960": {
        name: "Van Gogh",
        origin: "Zundert, Netherlands",
        expertise: "Post-Impressionist Art, Oil Painting",
        description:
            "Revolutionary post-impressionist painter known for his vivid colors and emotional impact.",
        imageUrl: "/static/images/Van Gogh.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FVincent.mp3?alt=media&token=a012326e-b29f-4118-b8b7-a4b023794487",
        ],
        quotes: [
            "I dream of painting and then I paint my dream.",
            "Great things are not done by impulse, but by a series of small things brought together.",
            "I would rather die of passion than of boredom.",
            "What is done in love is done well.",
            "For my part I know nothing with any certainty, but the sight of the stars makes me dream.",
        ],
        modelFormat: "glb",
    },
    "770e2d93-a6a4-41ed-aca1-8951327885b5": {
        name: "Amelia Earhart",
        origin: "Atchison, Kansas, United States",
        expertise: "Aviation, Women's Rights Advocacy",
        description:
            "Aviation pioneer and author who helped advance women's rights and aviation.",
        imageUrl: "/static/images/Amelia.png",
        // Use the artist-placed `EyeAnchor` node in the GLB for camera
        // framing instead of the eye-mesh-regex heuristics. Required
        // because Amelia's eye meshes resolve to an implausible IPD
        // (~2415 cm world-distance), so all heuristic paths reject and
        // the camera never moves to her — she renders outside the
        // viewport. See replit.md "Explicit EyeAnchor framing" gotcha.
        useEyeAnchor: true,
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FAmeliaV2.mp3?alt=media&token=976c701d-3fc1-4c16-a994-4e5b6e7e735b",
        ],
        quotes: [
            "The most difficult thing is the decision to act, the rest is merely tenacity.",
            "Adventure is worthwhile in itself.",
            "Courage is the price that life exacts for granting peace.",
            "Never do things others can do and will do if there are things others cannot do or will not do.",
            "The most effective way to do it, is to do it.",
        ],
        modelFormat: "glb",
    },
    "5a16ff56-6e03-414e-a89d-cda9d83fae60": {
        name: "Henry Ford",
        origin: "Springwells Township, Michigan",
        expertise: "Industrial Engineering, Business Innovation",
        description:
            "Industrial innovator who revolutionized automobile manufacturing and industry.",
        imageUrl: "/static/images/HenryFord.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FHenry%20Ford%20Greetings.mp3?alt=media&token=7c63e2b5-32d3-4099-bb76-edf99372188b",
        ],
        quotes: [
            "Whether you think you can, or you think you can't - you're right.",
            "Coming together is a beginning; keeping together is progress; working together is success.",
            "Failure is simply the opportunity to begin again, this time more intelligently.",
            "Quality means doing it right when no one is looking.",
            "The only real mistake is the one from which we learn nothing.",
        ],
    },

    "676f8b15-4a88-46b7-8842-8ade63743fb6": {
        name: "Test Bot A",
        origin: "Digital Creation",
        expertise: "Testing and Development",
        description: "A test character for development purposes.",
        imageUrl: "/static/images/Test.jpg",
    },

    "be0edc3a-e50d-4579-86b8-529db601fdd1": {
        name: "Rechard Feynman cc",
        origin: "Digital Creation",
        expertise: "Testing and Development",
        description: "A test character for development purposes.",
        imageUrl: "/static/images/RichardFeynman.png",
    },

    "031f2eea-64e5-441d-a37d-03d260cc43dc": {
        name: "Abraham Lincoln",
        origin: "Hodgenville, Kentucky, United States",
        expertise: "Law, Politics, Leadership",
        description:
            "16th President of the United States, known for leading the nation during the Civil War and working to end slavery.",
        imageUrl: "/static/images/Lincoln.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FAbrahamV2.mp3?alt=media&token=7e5d51cf-d7a7-42a5-8bd0-8e28b9a26451",
        ],
        quotes: [
            "Whatever you are, be a good one.",
            "The best way to predict your future is to create it.",
            "I'm a success today because I had a friend who believed in me and I didn't have the heart to let him down.",
            "My concern is not whether God is on our side; my greatest concern is to be on God's side.",
            "Nearly all men can stand adversity, but if you want to test a man's character, give him power.",
        ],
        //modelFormat: "glb",
    },
    "a59fe2c1-9046-405a-9a99-a7ddbaee13be": {
        name: "Adam Smith",
        origin: "Kirkcaldy, Fife, Scotland",
        expertise: "Economics, Philosophy",
        description:
            "Influential economist known for his works on classical economics, particularly 'The Wealth of Nations'.",
        imageUrl: "/static/images/AdamSmith.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FAdamSmithV2.mp3?alt=media&token=662fc1ed-058b-49ac-abab-fdfa6d0c28cc",
        ],
        quotes: [
            "The real tragedy of the poor is the poverty of their aspirations.",
            "Science is the great antidote to the poison of enthusiasm and superstition.",
            "No society can surely be flourishing and happy, of which the far greater part of the members are poor and miserable.",
            "It is not from the benevolence of the butcher, the brewer, or the baker that we expect our dinner, but from their regard to their own interest.",
            "Labor was the first price, the original purchase-money that was paid for all things.",
        ],
        useEyeAnchor: true,
        modelFormat: "glb",
    },
    "e3f55c33-3ff0-471f-b833-b59d7b145fc6": {
        name: "Al Capone",
        origin: "Brooklyn, New York, United States",
        expertise: "Organized Crime, Illegal Activities",
        description:
            "Notorious American gangster known for his leadership in organized crime during the Prohibition era.",
        imageUrl: "/static/images/AlCapone.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FAl%20Capone%20Greeting.mp3?alt=media&token=c53a6093-4bed-4ba2-97b7-0bae56b51f7f",
        ],
        quotes: [
            "You can get much further with a kind word and a gun than you can with a kind word alone.",
            "Once in the racket you're always in it.",
            "This American system of ours gives each and every one of us a great opportunity.",
            "I have built my organization upon fear.",
            "I am like any other man. All I do is supply a demand.",
        ],
        modelFormat: "glb",
    },
    "c4b9ac58-a66a-451e-b035-afabd8be50b9": {
        name: "Aristotle",
        origin: "Stagira, Greece",
        expertise: "Philosophy, Science, Metaphysics",
        description:
            "Ancient Greek philosopher and scientist who made significant contributions to many fields including metaphysics, logic, and ethics.",
        imageUrl: "/static/images/Test.jpg",
        // Without this, getModelFormat() defaults to "fbx" and the GLB
        // never loads (FBXLoader on glTF bytes) — the Marcus Aurelius bug.
        modelFormat: "glb",
    },
    "b8951237-698b-42b5-8b6e-ee381b91fb65": {
        name: "Confucius",
        origin: "Lu, China",
        expertise: "Philosophy, Ethics, Politics",
        description:
            "Chinese philosopher and politician known for his teachings on ethics, morality, and social relationships.",
        imageUrl: "/static/images/Confusius.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FConfucius.mp3?alt=media&token=b5eef433-9464-4bc1-9028-b6be07965bd9",
        ],
        quotes: [
            "It does not matter how slowly you go as long as you do not stop.",
            "Our greatest glory is not in never falling, but in rising every time we fall.",
            "The man who moves a mountain begins by carrying away small stones.",
            "Choose a job you love, and you will never have to work a day in your life.",
            "Real knowledge is to know the extent of one's ignorance.",
        ],
        modelFormat: "glb",
    },

    "9ef81498-3fb9-4175-a6d4-c2a02d71c640": {
        name: "Cyrus The Great",
        origin: "Anshan, Persis (modern-day Iran)",
        expertise: "Leadership, Military, Governance",
        description:
            "Founder of the Achaemenid Empire, known for his innovative leadership and conquests that created a multi-cultural empire.",
        imageUrl: "/static/images/Cyrus.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FCyrus%20the%20Great.mp3?alt=media&token=5c972207-0b35-4978-aa6a-cdf14ef9e2e4",
        ],
        quotes: [
            "Success always calls for greater generosity - though most people, lost in the darkness of their own egos, treat it as an occasion for greater greed.",
            "Diversity in counsel, unity in command.",
            "Freedom, dignity, wealth - these three together constitute the greatest happiness of humanity.",
            "Do not disturb the religious beliefs of your subjects, for this is the path to peace.",
            "There is nothing more disgraceful for any man who is a man than to found his honors on the labors of others.",
        ],
    },

    "8944783c-2e2b-4796-b92e-21dacca776bc": {
        name: "Leonardo da Vinci",
        origin: "Vinci, Italy",
        expertise: "Art, Science, Engineering",
        description:
            "Renaissance polymath known for his masterpieces of art such as the Mona Lisa and The Last Supper, as well as his groundbreaking work in various scientific fields.",
        imageUrl: "/static/images/DaVinci.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2Fda%20Vinci.mp3?alt=media&token=f445cff2-859d-4f0f-bcc3-58d9930539f1",
        ],
        quotes: [
            "Learning never exhausts the mind.",
            "Simplicity is the ultimate sophistication.",
            "The noblest pleasure is the joy of understanding.",
            "Obstacles cannot crush me; every obstacle yields to stern resolve.",
            "As a well-spent day brings happy sleep, so a life well spent brings happy death.",
        ],
        useEyeAnchor: true,
        //modelFormat: "glb",
    },

    "d6e3a3f1-d31e-4aab-8dd0-8b820932bd13": {
        name: "Enrico Fermi",
        origin: "Rome, Italy",
        expertise: "Theoretical Physics, Nuclear Physics",
        description:
            "Pioneering physicist known for his development of the first nuclear reactor and contributions to quantum theory and statistical mechanics.",
        imageUrl: "/static/images/Fermi.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2Fenricogreetings.mp3?alt=media&token=ed8168e6-2052-4dae-8e40-d9d22133ae65",
        ],
        quotes: [
            "Before I came here I was confused about this subject. Having listened to your lecture I am still confused. But on a higher level.",
            "There are two possible outcomes: if the result confirms the hypothesis, then you've made a measurement. If the result is contrary to the hypothesis, then you've made a discovery.",
            "It is no good to try to stop knowledge from going forward. Ignorance is never better than knowledge.",
            "Whatever Nature has in store for mankind, unpleasant as it may be, men must accept, for ignorance is never better than knowledge.",
            "Experimental confirmation of a prediction is merely a measurement. An experiment disproving a prediction is a discovery.",
        ],
    },
    "8f109c00-f7d2-4b9b-b44e-740a55e4339e": {
        name: "George Washington",
        origin: "Westmoreland County, Virginia, United States",
        expertise: "Leadership, Politics, Warfare",
        description:
            "First President of the United States, known for his leadership in the founding of the nation and setting many presidential precedents.",
        imageUrl: "/static/images/Test.jpg",
    },
    "4d7d2e17-b818-4144-b78b-629c688a71ac": {
        name: "Karl Marx",
        origin: "Trier, Germany",
        expertise: "Philosophy, Economics, Political Theory",
        description:
            "German philosopher, economist, and revolutionary socialist famous for his works on socialism and communism, including 'The Communist Manifesto'.",
        imageUrl: "/static/images/Marx.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FMarxV2.mp3?alt=media&token=982f324c-4d81-4d94-8f04-9b55777ef524",
        ],
        quotes: [
            "The philosophers have only interpreted the world, in various ways. The point, however, is to change it.",
            "History repeats itself, first as tragedy, second as farce.",
            "From each according to his abilities, to each according to his needs.",
            "The production of too many useful things results in too many useless people.",
            "Reason has always existed, but not always in a reasonable form.",
        ],
        //useEyeAnchor: true,

        //modelFormat: "glb",
    },

    "2baa0479-7e38-4473-a2f2-1ad54149dbac": {
        name: "Muhammad Ali",
        origin: "Louisville, Kentucky, United States",
        expertise: "Boxing, Activism",
        description:
            "American professional boxer and cultural icon, known for his skills in the ring and his outspoken stance on social issues.",
        imageUrl: "/static/images/Ali.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FMuhammad%20Ali.mp3?alt=media&token=8988b010-8a64-4792-85ef-ea5f54c4f96f",
        ],
        quotes: [
            "Float like a butterfly, sting like a bee.",
            "I hated every minute of training, but I said, 'Don't quit. Suffer now and live the rest of your life as a champion.'",
            "He who is not courageous enough to take risks will accomplish nothing in life.",
            "Impossible is just a word thrown around by small men who find it easier to live in the world they've been given than to explore the power they have to change it.",
            "Service to others is the rent you pay for your room here on earth.",
        ],
        modelFormat: "glb",
    },

    "9039a447-b6eb-4de2-9927-0b0c6476c090": {
        name: "Malcolm X",
        origin: "Omaha, Nebraska, United States",
        expertise: "Civil Rights, Human Rights, Oratory",
        description:
            "American Muslim minister and human rights activist, a fearless and uncompromising voice for Black empowerment and self-determination.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/21/Malcolm_X_1963_press_photo.jpg/330px-Malcolm_X_1963_press_photo.jpg",
    },
    "e97568c5-1daa-4a95-b4ae-0fe374c6b083": {
        name: "Marcus Aurelius",
        origin: "Rome, Roman Empire",
        expertise: "Philosophy, Leadership, Stoicism",
        description:
            "Roman Emperor and philosopher known for his Stoic writings and leadership during the Roman Empire's challenging periods.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ec/MSR-ra-61-b-1-DM.jpg/330px-MSR-ra-61-b-1-DM.jpg",
        // Full-body "teacher at the front of the room" framing:
        // loose box (extra headroom) + portrait shifted up so the
        // torso stays in frame at the default camera distance.
        framingHeadroomMin: 55,
        framingYOffset: 18,
        // CRITICAL: without this field getModelFormat() defaults to "fbx"
        // and /lab feeds the GLB to FBXLoader — the model never loads on
        // ANY machine (the "Marcus never rendered" bug; /testcharacter
        // byte-sniffs so it worked there, masking this).
        modelFormat: "glb",
    },

    "d0c97034-328e-4538-8107-a9825d0c1594": {
        name: "Martin Luther King",
        origin: "Atlanta, Georgia, United States",
        expertise: "Civil Rights, Leadership, Public Speaking",
        description:
            "American civil rights leader who advocated for equality and nonviolent protest, famously delivering the 'I Have a Dream' speech.",
        imageUrl: "/static/images/Test.jpg",
    },

    "7716e2f6-eb48-424d-a962-252b15ca634e": {
        name: "Newton",
        origin: "Woolsthorpe, Lincolnshire, England",
        expertise: "Physics, Mathematics, Astronomy",
        description:
            "English mathematician, physicist, astronomer, and author, widely recognized as one of the most influential scientists of all time and a key figure in the scientific revolution.",
        imageUrl: "/static/images/Test.jpg",
    },
    "5a42d57c-6e2b-4ba8-bd32-838a1a5c5e6b": {
        name: "Niccolo Machiavelli",
        origin: "Florence, Italy",
        expertise: "Political Philosophy, History, Diplomacy",
        description:
            "Renowned Italian diplomat, philosopher, and historian, best known for his political treatise, 'The Prince'.",
        imageUrl: "/static/images/Machiavelli.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FMachiavelliGreeting2.mp3?alt=media&token=10edc103-048f-4d1b-9017-ee31e7b23924",
        ],
        quotes: [
            "It is better to be feared than loved, if you cannot be both.",
            "Everyone sees what you appear to be, few experience what you really are.",
            "The lion cannot protect himself from traps, and the fox cannot defend himself from wolves. One must therefore be a fox to recognize traps, and a lion to frighten wolves.",
            "Politics have no relation to morals.",
            "Never attempt to win by force what can be won by deception.",
        ],
        modelFormat: "glb",
    },

    "59c67a42-8c1e-45b8-aa41-b6fe6b3f5629": {
        name: "George Orwell",
        origin: "Motihari, British India",
        expertise: "Writing, Political Commentary, Literature",
        description:
            "English novelist, essayist, journalist, and critic known for his works addressing social injustice and totalitarianism, such as '1984' and 'Animal Farm'.",
        imageUrl: "/static/images/Orwell.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FGeorge%20Orwell.mp3?alt=media&token=f1758545-0f10-4a6e-921b-4f4ed7678f44",
        ],
        quotes: [
            "In a time of deceit, telling the truth is a revolutionary act.",
            "If liberty means anything at all, it means the right to tell people what they do not want to hear.",
            "All animals are equal, but some animals are more equal than others.",
            "The most effective way to destroy people is to deny and obliterate their own understanding of their history.",
            "Who controls the past controls the future. Who controls the present controls the past.",
        ],
        modelFormat: "glb",
    },

    "3542f19e-4e07-4828-8f9d-f330e4d6d660": {
        name: "Plato",
        origin: "Athens, Greece",
        expertise: "Philosophy, Political Theory, Mathematics",
        description:
            "Ancient Greek philosopher, student of Socrates, and founder of the Academy in Athens. Known for his works in philosophy, including 'The Republic'.",
        imageUrl: "/static/images/Plato.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FPlato.mp3?alt=media&token=557e09b1-5aed-4782-bbec-edc0f4b294bc",
        ],
        quotes: [
            "Wise men speak because they have something to say; fools because they have to say something.",
            "Courage is knowing what not to fear.",
            "The beginning is the most important part of the work.",
            "We can easily forgive a child who is afraid of the dark; the real tragedy of life is when men are afraid of the light.",
            "At the touch of love everyone becomes a poet.",
        ],
        // CRITICAL: without this field getModelFormat() defaults to "fbx"
        // and /lab feeds Plato's GLB (PlatoGLBFNewFace, via the model-source
        // override plane) to FBXLoader — the model never loads on ANY
        // machine (same wrong-loader trap as Marcus Aurelius; /testcharacter
        // byte-sniffs so it renders there, masking this).
        modelFormat: "glb",
    },

    "d75e63e0-2b2b-43ef-933f-589e8d6818c0": {
        name: "Salvador Dali",
        origin: "Figueres, Catalonia, Spain",
        expertise: "Surrealism, Painting, Sculpture",
        description:
            "Influential Spanish surrealist artist known for his dream-like and bizarre images, as well as his eccentric personality.",
        imageUrl: "/static/images/SalvadorDali.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FSalvador.mp3?alt=media&token=693162cc-4d92-464d-b7ee-c382b488240d",
        ],
        quotes: [
            "Have no fear of perfection - you'll never reach it.",
            "A true artist is not one who is inspired, but one who inspires others.",
            "The difference between a madman and me is that I am not mad.",
            "Give me two hours a day of activity, and I'll take the other twenty-two in dreams.",
            "Surrealism is destructive, but it destroys only what it considers to be shackles limiting our vision.",
        ],
        //useEyeAnchor: true,
        //modelFormat: "glb",
    },

    "548fe879-e63c-4d6c-a377-525f4f9e12c8": {
        name: "Satoshi Nakamoto",
        origin: "Unknown",
        expertise: "Cryptocurrency, Blockchain Technology",
        description:
            "Pseudonymous person or group known for creating Bitcoin and implementing the first blockchain.",
        imageUrl: "/static/images/Test.jpg",
    },
    "d9f9af52-ae93-4bd2-abd4-0b7032844ddd": {
        name: "Nikola Tesla",
        origin: "Smiljan, Croatian Military Frontier (modern-day Croatia)",
        expertise: "Electrical Engineering, Mechanical Engineering, Physics",
        description:
            "Pioneering inventor and engineer known for his contributions to the design of the modern alternating current (AC) electricity supply system.",
        imageUrl: "/static/images/Test.jpg",
    },

    "7c7ac397-c36e-4218-92d4-f437d1959cca": {
        name: "Euclid",
        origin: "Alexandria, Egypt",
        expertise: "Geometry, Number theory, Mathematical proof, Optics",
        description:
            "The Alexandrian teacher who gathered the scattered mathematics of the Greek world into The Elements, a single logical chain of definitions, axioms, and proofs that shaped reasoning for two thousand years.",
        imageUrl: "/static/images/Euclid.png",
        greetings: [],
        quotes: [
            "There is no royal road to geometry.",
            "The whole is greater than the part.",
            "Things which are equal to the same thing are also equal to one another.",
            "A point is that which has no part.",
            "That which was to be demonstrated.",
        ],
        modelFormat: "glb",
    },

    "f9f3f836-00c6-4160-8416-4b71b53f1307": {
        name: "Srinivasa Ramanujan",
        origin: "Erode / Kumbakonam, Tamil Nadu, India",
        expertise: "Number theory, Infinite series, Partitions, Continued fractions",
        description:
            "A self-taught Indian mathematician whose intuition produced thousands of astonishing identities, many decades ahead of their time. His 1913 letters to G. H. Hardy launched one of history's great mathematical partnerships.",
        imageUrl: "/static/images/SrinivasaRamanujan.png",
        greetings: [],
        quotes: [
            "An equation for me has no meaning unless it expresses a thought of God.",
            "I have not trodden through the conventional course, but I am striking out a new path for myself.",
            "No, it is a very interesting number; it is the smallest number expressible as the sum of two cubes in two different ways.",
            "While asleep, I had an unusual experience. There was a red screen formed by flowing blood, and a hand began to write on it.",
            "Sir, an equation has no meaning for me unless it expresses a thought of God.",
        ],
        modelFormat: "glb",
    },

    "5802e813-e715-491d-a0fd-92dc643c458e": {
        name: "Blaise Pascal",
        origin: "Clermont-Ferrand, France",
        expertise: "Probability, Geometry, Physics of fluids, Christian philosophy",
        description:
            "A French prodigy who co-founded probability theory, built a mechanical calculator at nineteen, proved the weight of air, then turned his brilliance to God and the human heart.",
        imageUrl: "/static/images/BlaisePascal.png",
        greetings: [],
        quotes: [
            "The heart has its reasons which reason knows nothing of.",
            "Man is but a reed, the weakest in nature, but he is a thinking reed.",
            "All of humanity's problems stem from man's inability to sit quietly in a room alone.",
            "The eternal silence of these infinite spaces terrifies me.",
            "We know the truth not only by the reason, but also by the heart.",
        ],
        modelFormat: "glb",
    },

    "191e6103-a42c-4132-926c-230e7b1be5b6": {
        name: "Jacob Bernoulli",
        origin: "Basel, Switzerland",
        expertise: "Probability theory, infinite series, differential calculus, the calculus of variations",
        description:
            "The Basel mathematician who tamed chance into law, proving that patient observation reveals the odds beneath the noise. He gave us the law of large numbers, the Bernoulli numbers, and a spiral he loved enough to carry into eternity.",
        imageUrl: "/static/images/JacobBernoulli.png",
        greetings: [],
        quotes: [
            "Even as the finite encloses an infinite series and in the unlimited limits appear, so the soul of immensity dwells in minutia.",
            "I recognize the lion by his claw.",
            "For even the most stupid of men is convinced that the more observations have been made, the less danger there is of wandering from one's goal.",
            "Though changed, I shall arise the same.",
            "If the numbers are large enough, the frequency will reveal the probability that Providence has hidden within.",
        ],
        modelFormat: "glb",
    },

    "6bcf3678-e232-46c9-996a-67beb3fd2dbf": {
        name: "Al-Khwarizmi",
        origin: "Khwarazm (modern Uzbekistan), Abbasid Caliphate",
        expertise: "Algebra, Arithmetic, Astronomy, Geography",
        description:
            "The Baghdad scholar whose book on al-jabr founded algebra as a discipline, and whose Latinized name gave us the word 'algorithm'.",
        imageUrl: "/static/images/AlKhwarizmi.png",
        greetings: [],
        quotes: [
            "When I consider what people generally want in calculating, I found that it always is a number.",
            "That which restores the broken, and completes the incomplete, I call al-jabr.",
            "The learned in times gone by have written many books to be of use to posterity; I follow them.",
            "I have composed a short work on calculating by completion and balancing, confining it to what is easiest and most useful.",
            "A number is either roots, or squares, or numbers alone; from these all problems may be built.",
        ],
        modelFormat: "glb",
    },

    "14836d13-ace6-4bf8-be26-26fbb80ed9bc": {
        name: "Socrates",
        origin: "Athens, Greece",
        expertise: "Ethics, dialectic, epistemology, self-knowledge",
        description:
            "The barefoot Athenian who turned the marketplace into a schoolroom, asking relentless questions until easy certainties fell apart. He wrote nothing, yet reshaped how the West thinks about virtue and truth.",
        imageUrl: "/static/images/Socrates.png",
        greetings: [],
        quotes: [
            "The unexamined life is not worth living.",
            "I know that I know nothing.",
            "There is only one good, knowledge, and one evil, ignorance.",
            "Wisdom begins in wonder.",
            "Be as you wish to seem.",
        ],
        modelFormat: "glb",
    },

    "3841a6bb-f977-453d-92f5-6cec1267546f": {
        name: "Sigmund Freud",
        origin: "Freiberg, Moravia (raised in Vienna, Austria)",
        expertise: "Psychoanalysis, dream interpretation, the unconscious, human sexuality",
        description:
            "The Viennese physician who turned dreams, slips of the tongue, and childhood into a science of the hidden mind. He listened where others looked away.",
        imageUrl: "/static/images/SigmundFreud.png",
        greetings: [],
        quotes: [
            "The interpretation of dreams is the royal road to a knowledge of the unconscious activities of the mind.",
            "Where id was, there ego shall be.",
            "Sometimes a cigar is just a cigar.",
            "Being entirely honest with oneself is a good exercise.",
            "The ego is not master in its own house.",
        ],
        modelFormat: "glb",
    },

    "c0063a05-916b-45d0-bc2c-c63210414b17": {
        name: "Ada Lovelace",
        origin: "London, England",
        expertise: "Mathematics, Computing, Analytical engines, Symbolic logic",
        description:
            "A visionary English mathematician who saw that Charles Babbage's Analytical Engine could do far more than crunch numbers. Her 1843 Notes contain what many call the first published computer algorithm.",
        imageUrl: "/static/images/AdaLovelace.png",
        greetings: [],
        quotes: [
            "The Analytical Engine weaves algebraical patterns just as the Jacquard loom weaves flowers and leaves.",
            "That brain of mine is something more than merely mortal, as time will show.",
            "The Analytical Engine has no pretensions whatever to originate anything. It can do whatever we know how to order it to perform.",
            "I never am really satisfied that I understand anything; because, understand it well as I may, my comprehension can only be an infinitesimal fraction of all I want to understand.",
            "Imagination is the Discovering Faculty, pre-eminently. It is that which penetrates into the unseen worlds around us, the worlds of Science.",
        ],
        modelFormat: "glb",
    },

    "7f61d092-99e4-48e6-bf80-7aa2b7bd98ff": {
        name: "Carl Friedrich Gauss",
        origin: "Brunswick, Duchy of Brunswick-Wolfenbuttel (Germany)",
        expertise: "Number theory, Geodesy, Astronomy, Magnetism",
        description:
            "A German prodigy whose reach spanned pure and applied science, from the deepest truths of number theory to surveying the Earth and measuring its magnetism. Known as the Prince of Mathematicians, he set a standard of rigor that still defines the field.",
        imageUrl: "/static/images/CarlFriedrichGauss.png",
        greetings: [],
        quotes: [
            "Mathematics is the queen of the sciences, and number theory is the queen of mathematics.",
            "Pauca sed matura. Few, but ripe.",
            "It is not knowledge, but the act of learning, not possession but the act of getting there, which grants the greatest enjoyment.",
            "I have had my results for a long time, but I do not yet know how I am to arrive at them.",
            "I confess that Fermat's Theorem as an isolated proposition has very little interest for me, for I could easily lay down a multitude of such propositions.",
        ],
        modelFormat: "glb",
    },

    "127cd993-f16e-4659-84fa-882cba633097": {
        name: "Galileo Galilei",
        origin: "Pisa, Italy",
        expertise: "Telescopic astronomy, Mechanics of motion, Mathematical physics, Instrument making",
        description:
            "An Italian astronomer, physicist, and engineer who turned a new spyglass on the night sky, found mountains on the Moon and moons around Jupiter, and paid with his freedom for insisting the Earth moves.",
        imageUrl: "/static/images/GalileoGalilei.png",
        greetings: [],
        quotes: [
            "And yet it moves.",
            "Measure what is measurable, and make measurable what is not so.",
            "I do not feel obliged to believe that the same God who has endowed us with sense, reason, and intellect has intended us to forgo their use.",
            "In questions of science, the authority of a thousand is not worth the humble reasoning of a single individual.",
            "The Sun, with all those planets revolving around it, can still ripen a bunch of grapes as if it had nothing else in the universe to do.",
        ],
        modelFormat: "glb",
    },

    "94b6efbe-0aaa-47c7-bd3f-6d4f441d767f": {
        name: "Grace Hopper",
        origin: "New York City, New York, United States",
        expertise: "Compilers, programming languages, mathematics, U.S. Navy computing",
        description:
            "The Navy rear admiral and mathematician who taught computers to understand words, invented the first compiler, and lit the path from FLOW-MATIC to COBOL. She carried nanosecond wires in her pocket to make time visible.",
        imageUrl: "/static/images/GraceHopper.png",
        greetings: [],
        quotes: [
            "It is easier to ask forgiveness than it is to get permission.",
            "The most dangerous phrase in the language is, 'We've always done it this way.'",
            "A ship in port is safe, but that's not what ships are built for.",
            "Humans are allergic to change. They love to say, 'We've always done it this way.' I try to fight that.",
            "One accurate measurement is worth a thousand expert opinions.",
        ],
        modelFormat: "glb",
    },

    "c93478a1-ed2f-4f1a-81f6-3fa474a21566": {
        name: "Joseph-Louis Lagrange",
        origin: "Turin, Kingdom of Sardinia (now Italy)",
        expertise: "Analytical mechanics, calculus of variations, number theory, celestial mechanics",
        description:
            "The mathematician who rebuilt all of mechanics on pure algebra and analysis, proudly boasting that his masterwork contained not a single diagram. From Turin to Berlin to Paris, he refined the equations of motion, mapped the balance points of the heavens, and steered the birth of the metric system.",
        imageUrl: "/static/images/JosephLouisLagrange.png",
        greetings: [],
        quotes: [
            "One does not find in this work any figures. The methods I set forth require neither constructions nor geometrical or mechanical reasonings, but only algebraic operations.",
            "It took the mob only a moment to remove his head; a century will not suffice to produce another like it.",
            "When we ask advice, we are usually looking for an accomplice.",
            "As long as algebra and geometry proceeded along separate paths, their advance was slow and their applications limited. But when these sciences joined company, they drew from each other fresh vitality and thenceforward marched on at a rapid pace.",
            "I do not know.",
        ],
        modelFormat: "glb",
    },

    "5a39bed2-a551-4a03-98a2-46104a3324bf": {
        name: "Leonhard Euler",
        origin: "Basel, Switzerland",
        expertise: "Mathematical analysis, number theory, graph theory, mechanics and astronomy",
        description:
            "The most prolific mathematician who ever lived, a Basel-born genius whose pen shaped the very symbols we still calculate with. He gave us e, i, f(x), and the sum sign, solved the Basel problem, and invented graph theory while strolling across the bridges of Konigsberg.",
        imageUrl: "/static/images/LeonhardEuler.png",
        greetings: [],
        quotes: [
            "Read Euler, read Euler, he is the master of us all.",
            "Now I will have fewer distractions.",
            "Nothing takes place in the world whose meaning is not that of some maximum or minimum.",
            "Logic is the foundation of the certainty of all the knowledge we acquire.",
            "For since the fabric of the universe is most perfect and the work of a most wise Creator, nothing at all takes place in the universe in which some rule of maximum or minimum does not appear.",
        ],
        modelFormat: "glb",
    },

    "2707ca40-df64-4258-97c8-67d3695e8a42": {
        name: "Brahmagupta",
        origin: "Bhillamala, Rajasthan, India",
        expertise: "Arithmetic of zero, Negative numbers, Algebra, Astronomy",
        description:
            "The Indian astronomer-mathematician who first treated zero as a number in its own right and gave rules for the fortunes and debts we now call positive and negative, all set down in Sanskrit verse in his masterwork the Brahmasphutasiddhanta.",
        imageUrl: "/static/images/Brahmagupta.png",
        greetings: [],
        quotes: [
            "The sum of zero and a negative is negative, of a positive and zero is positive; the sum of zero and zero is zero.",
            "A debt subtracted from nothing becomes a fortune, and a fortune subtracted from nothing becomes a debt.",
            "The product of a fortune and a debt is a debt; the product of two debts is a fortune.",
            "As the sun eclipses the stars by its brilliance, so a learned man may eclipse the fame of others in assemblies by solving these problems.",
            "Zero divided by zero is nothing, and a quantity divided by zero is a fraction with zero for its denominator.",
        ],
        modelFormat: "glb",
    },

    "e617290b-bda0-4771-91ac-3fe6939dd412": {
        name: "Benjamin Franklin",
        origin: "Boston, Massachusetts (settled in Philadelphia, Pennsylvania)",
        expertise: "Printing and publishing, electricity, diplomacy, civic invention",
        description:
            "The runaway apprentice who became a printer, a scientist who tamed lightning, and a diplomat who helped invent a nation. He turned curiosity and common sense into a way of life.",
        imageUrl: "/static/images/BenjaminFranklin.png",
        greetings: [],
        quotes: [
            "An investment in knowledge pays the best interest.",
            "Early to bed and early to rise makes a man healthy, wealthy, and wise.",
            "Tell me and I forget, teach me and I may remember, involve me and I learn.",
            "Well done is better than well said.",
            "By failing to prepare, you are preparing to fail.",
        ],
        modelFormat: "glb",
    },

    "a2a43496-9a83-4444-813e-18a1930296e6": {
        name: "Omar Khayyam",
        origin: "Nishapur, Khorasan (modern Iran)",
        expertise: "Algebra, Astronomy, Geometry, Poetry",
        description:
            "The Persian polymath who solved cubic equations with cones, reformed the calendar to astonishing accuracy, and, in the West, became beloved as the poet of the Rubaiyat.",
        imageUrl: "/static/images/OmarKhayyam.png",
        greetings: [],
        quotes: [
            "The Moving Finger writes; and, having writ, moves on.",
            "A Book of Verses underneath the Bough, a Jug of Wine, a Loaf of Bread, and Thou.",
            "Whoever thinks algebra is a trick in obtaining unknowns has thought it in vain.",
            "Be happy for this moment. This moment is your life.",
            "And that inverted Bowl we call the Sky, whereunder crawling coop'd we live and die.",
        ],
        modelFormat: "glb",
    },

    "78376372-3d71-4fce-8650-82b5be2c006b": {
        name: "Diophantus",
        origin: "Alexandria, Egypt",
        expertise: "Algebra, Number theory, Equations, Symbolic notation",
        description:
            "The Alexandrian mathematician whose Arithmetica turned mathematics from geometry of shapes into the art of solving equations, inventing a shorthand for the unknown and posing puzzles that would haunt number theory for centuries.",
        imageUrl: "/static/images/Diophantus.png",
        greetings: [],
        quotes: [
            "A number to be added, and a number to be taken away: let the unknown stand for what we seek.",
            "The problem is not to measure the field, but to find the number that satisfies the condition.",
            "Let us call the sought quantity the arithmos, and reckon with it as with any other.",
            "Seek only the numbers that answer, and leave the rest aside.",
            "Where two conditions meet in one unknown, there the solution hides.",
        ],
        modelFormat: "glb",
    },

    "ca6ebdca-5151-4a58-8523-56d25fce5dfa": {
        name: "William Playfair",
        origin: "Dundee, Scotland",
        expertise: "Statistical graphics, political economy, engineering, pamphleteering",
        description:
            "The Scottish engineer and adventurer who invented the line graph, the bar chart, and the pie chart, teaching merchants and ministers to see numbers rather than merely read them. A restless rogue whose schemes failed as often as his charts succeeded.",
        imageUrl: "/static/images/WilliamPlayfair.png",
        greetings: [],
        quotes: [
            "As much information may be obtained in five minutes as would require whole days to imprint on the memory, in a lasting manner, by a table of figures.",
            "The advantage proposed by this method is not that of giving a more accurate statement than by figures, but it is to give a more simple and permanent idea.",
            "Men of great rank, or active business, can only pay attention to general outlines; nor is attention to particulars of use, any farther than as they give a general information.",
            "Making an appeal to the eye when proportion and magnitude are concerned is the best and readiest method of conveying a distinct idea.",
            "Whatever presents itself to the eye conveys a lasting impression to the mind, while that which is only stated in figures is soon forgotten.",
        ],
        modelFormat: "glb",
    },

    "e3428624-bd1e-48b9-ae8c-c66ba68879cc": {
        name: "George Boole",
        origin: "Lincoln, England",
        expertise: "Symbolic logic, Algebra, Differential equations, Philosophy of mind",
        description:
            "A self-taught English mathematician who turned the laws of reasoning into algebra, showing that thought itself could be calculated with symbols, ones and zeros. His 1854 work The Laws of Thought became the hidden foundation of the digital age.",
        imageUrl: "/static/images/GeorgeBoole.png",
        greetings: [],
        quotes: [
            "The design of the following treatise is to investigate the fundamental laws of those operations of the mind by which reasoning is performed.",
            "No general method for the solution of questions in the theory of probabilities can be established which does not explicitly recognise the mental phenomena.",
            "It is not of the essence of mathematics to be conversant with the ideas of number and quantity.",
            "That language is an instrument of human reason, and not merely a medium for the expression of thought, is a truth generally admitted.",
            "The laws we have to examine are the laws of one of the most important of our mental faculties.",
        ],
        modelFormat: "glb",
    },

    "3140d634-c7ca-4bda-af36-e1b49978b201": {
        name: "Thomas Bayes",
        origin: "London, England",
        expertise: "Inverse probability, theory of chances, mathematical analysis, Newtonian calculus",
        description:
            "A quiet Presbyterian minister at Tunbridge Wells who published almost nothing in life yet left behind a single essay that taught the world to reason backward from evidence to cause, the seed of what we now call Bayesian inference.",
        imageUrl: "/static/images/ThomasBayes.png",
        greetings: [],
        quotes: [
            "Given the number of times an unknown event has happened and failed, to find the chance that its probability lies between any two named degrees.",
            "We must reason from what we have observed to what we have reason to expect.",
            "The fluxion is no ghost of a departed quantity, but the sober measure of a thing in the very act of changing.",
            "I would rather be sure of my ground than swift to publish it.",
            "Every fresh observation ought to move our expectation, a little or a great deal, according to its weight.",
        ],
        modelFormat: "glb",
    },

    "c73fed54-81e3-46ec-9d7c-3d11d05b142d": {
        name: "Eratosthenes",
        origin: "Cyrene, Libya",
        expertise: "Geography, Mathematics, Astronomy, Number theory",
        description:
            "The chief librarian of Alexandria who calculated the circumference of the Earth from the length of a noon shadow, invented the sieve for finding primes, and drew the first grid of latitude and longitude across the known world.",
        imageUrl: "/static/images/Eratosthenes.png",
        greetings: [],
        quotes: [
            "The Earth is a sphere, and its measure lies within the reach of reason.",
            "Give me a well and a shadow, and I will tell you the size of the world.",
            "A single stade of error at the well becomes a thousand across the globe.",
            "Sail west from the Pillars of Heracles and you would reach India, were the ocean not so vast.",
            "Judge men not by Greek or barbarian, but by their virtue and their vice.",
        ],
        modelFormat: "glb",
    },

    "ece0ab37-37c1-4ead-8222-fbb5b185dbe2": {
        name: "Alessandro Volta",
        origin: "Como, Lombardy, Italy",
        expertise: "Electricity, chemistry, instrument design, natural philosophy",
        description:
            "The patient experimenter of Como who stacked silver and zinc into the world's first battery, giving humanity a steady, controllable electric current. His name lives on in every volt.",
        imageUrl: "/static/images/AlessandroVolta.png",
        greetings: [],
        quotes: [
            "The language of experiment is more authoritative than any reasoning.",
            "I have long held that the seat of the electric fluid lies in the contact of dissimilar metals.",
            "It is a proof that a continuous circulation of the electric fluid takes place.",
            "One must not multiply causes beyond what the facts require.",
            "The apparatus, however new in its effects, is at bottom of a very simple construction.",
        ],
        modelFormat: "glb",
    },

    "d13b5a81-ed77-4a25-8a01-a76a0165b349": {
        name: "Archimedes",
        origin: "Syracuse, Sicily",
        expertise: "Geometry, Hydrostatics, Mechanics, Military engineering",
        description:
            "The Syracusan who leapt from his bath shouting Eureka, measured the sphere against its cylinder, and turned pure geometry into engines that held a Roman fleet at bay.",
        imageUrl: "/static/images/Archimedes.png",
        greetings: [],
        quotes: [
            "Eureka! I have found it.",
            "Give me a place to stand, and I will move the earth.",
            "Do not disturb my circles.",
            "There are things which seem incredible to most men who have not studied mathematics.",
            "The shortest distance between two points is a straight line.",
        ],
        modelFormat: "glb",
    },

    "8f67d579-41c2-456d-900b-fccbcbf46bd3": {
        name: "François Viète",
        origin: "Fontenay-le-Comte, Poitou, France",
        expertise: "Algebra, trigonometry, cryptanalysis, royal counsel",
        description:
            "The French lawyer and royal codebreaker who taught mathematics to speak in letters, turning tangled recipes of numbers into a general symbolic art he called analysis. He solved kings' ciphers by day and chased infinity for pleasure by night.",
        imageUrl: "/static/images/FrancoisViete.png",
        greetings: [],
        quotes: [
            "There is no problem that cannot be solved.",
            "The analytic art claims for itself the proud problem of problems, which is to leave no problem unsolved.",
            "Let magnitude be compared with magnitude, and let there be a law of homogeneity.",
            "I set forth the art by which the false may be told from the true.",
            "Mathematics received as if newborn from me a form worthy of a science.",
        ],
        modelFormat: "glb",
    },

    "ec6c5b20-8ac6-42bd-b291-f248cb3f0ebc": {
        name: "Felix Klein",
        origin: "Dusseldorf, Prussia (now Germany)",
        expertise: "Geometry, group theory, complex analysis, mathematics education",
        description:
            "The German geometer who taught mathematics to see geometry through the lens of symmetry, wrote the visionary Erlangen Program, and built Gottingen into the world capital of mathematics.",
        imageUrl: "/static/images/FelixKlein.png",
        greetings: [],
        quotes: [
            "Everyone knows what a curve is, until he has studied enough mathematics to become confused.",
            "A geometry is the study of those properties of a space that are left unchanged by a group of transformations.",
            "Mathematics in general is fundamentally the science of self-evident things.",
            "The greatest mathematicians, as Archimedes, Newton, and Gauss, always united theory and applications in equal measure.",
            "Projective geometry has opened up for us with the greatest facility new territories in our science, and has been rightly called a royal road to its own particular field of knowledge.",
        ],
        modelFormat: "glb",
    },

    "44f87c0b-ca22-4225-9fea-8d823e6a0160": {
        name: "Warren Buffett",
        origin: "Omaha, Nebraska, United States",
        expertise: "Investment, Business, Philanthropy",
        description:
            "Renowned investor and business magnate, known for his leadership in Berkshire Hathaway and philanthropy efforts.",
        imageUrl: "/static/images/Warren Buffett.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FWarrenV2.mp3?alt=media&token=b3a2b66c-fdcf-4715-948f-d16466719159",
        ],
        quotes: [
            "The best investment you can make is in yourself.",
            "Price is what you pay. Value is what you get.",
            "It takes 20 years to build a reputation and five minutes to ruin it.",
            "Risk comes from not knowing what you're doing.",
            "The difference between successful people and really successful people is that really successful people say no to almost everything.",
        ],
        modelFormat: "glb",
    },

    "581e7293-43cf-45ea-9fb3-0955c563cbe2": {
        name: "Genghis Khan",
        origin: "Khentii Mountains, Mongolia",
        expertise: "Military Strategy, Leadership, Conquests",
        description:
            "Founder and first Great Khan of the Mongol Empire, known for his unification of the Mongolian steppe tribes and extensive conquests across Asia and Europe.",
        imageUrl: "/static/images/GenghisKhan.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FGenghis%20Khan%20Greeting.mp3?alt=media&token=b01e2f19-db41-48fe-99ce-ff9f09914d55",
        ],
        quotes: [
            "I am the punishment of God. If you had not committed great sins, God would not have sent a punishment like me upon you.",
            "An action committed in anger is an action doomed to failure.",
            "The strength of a wall is neither greater nor less than the courage of the men who defend it.",
            "If you're afraid - don't do it, if you're doing it - don't be afraid!",
            "Be of one mind and one faith, that you may conquer your enemies and lead long and happy lives.",
        ],
        modelFormat: "glb",
    },
    "278dddd6-b4a8-4303-a41d-be6219dbc907": {
        name: "Albert Einstein",
        origin: "Ulm, Kingdom of Württemberg, German Empire",
        expertise: "Theoretical Physics, Quantum Mechanics, Relativity",
        description:
            "Renowned physicist known for developing the theory of relativity, one of the two pillars of modern physics, alongside quantum mechanics.",
        imageUrl: "/static/images/Einstein.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FAlbert%20Greeting.mp3?alt=media&token=852a17fa-c5ed-44ec-8d7e-6876a4e9f0a4",
        ],
        quotes: [
            "Imagination is more important than knowledge. Knowledge is limited. Imagination embraces the entire world.",
            "Life is like riding a bicycle. To keep your balance, you must keep moving.",
            "Try not to become a man of success, but rather try to become a man of value.",
            "The important thing is not to stop questioning. Curiosity has its own reason for existing.",
            "Anyone who has never made a mistake has never tried anything new.",
        ],
        modelFormat: "glb",
    },
    "3f14429d-b2d4-4b99-8a05-58eed567c0d3": {
        name: "Mansa Musa",
        origin: "Niani, Mali Empire",
        expertise: "Wealth, Leadership, Empire Building",
        description:
            "Emperor of the Mali Empire during the 14th century, renowned for his immense wealth and expansion of the empire through trade and military conquests.",
        imageUrl: "/static/images/Mansa Musa.png",
        greetings: [
            "https://firebasestorage.googleapis.com/v0/b/akadimia-dev.appspot.com/o/ForWebGreeting%2FMansa%20Musa%20Greetings.mp3?alt=media&token=f0e0dc1f-eeb9-4e55-9e05-8a9d242d25be",
        ],
        quotes: [
            "Wealth is not meant to be hoarded but shared to uplift communities and spread prosperity.",
            "A kingdom built on knowledge and trade shall outlast one built solely on conquest.",
            "Gold may fill your coffers, but education fills your empire with wisdom.",
            "The greatest journeys bring not just riches, but understanding between peoples.",
            "True power lies not in what you possess, but in what you give to advance your people.",
        ],
        modelFormat: "glb",
    },

    // Function to get character by ID
    getCharacterById: function (id) {
        if (this[id]) return this[id];
        // Doc-born characters (character_documents control plane, Task #413):
        // synthesize an entry from the injected payload. The injected model
        // format MUST win — the getModelFormat() default is "fbx", and a GLB
        // routed through FBXLoader never renders (the Marcus incident class).
        try {
            const d = (window.__docCharacters || {})[id];
            if (d) {
                return {
                    name: d.name || d.slug || "Unknown",
                    description: d.description || "",
                    field: "",
                    era: "",
                    expertise: d.expertise || "",
                    imageUrl: d.portrait || "",
                    modelFormat: d.format || "glb",
                    greetings: [],
                    quotes: Array.isArray(d.quotes) ? d.quotes : [],
                    docBorn: true,
                };
            }
        } catch (e) {}
        return null;
    },

    // Function to get character by name
    getCharacterByName: function (name) {
        for (const id in this) {
            if (
                this.hasOwnProperty(id) &&
                typeof this[id] === "object" &&
                this[id].name === name
            ) {
                return this[id];
            }
        }
        return null;
    },

    // Function to get characters by field
    getCharactersByField: function (field) {
        const result = [];
        for (const id in this) {
            if (
                this.hasOwnProperty(id) &&
                typeof this[id] === "object" &&
                this[id].field === field
            ) {
                result.push(this[id]);
            }
        }
        return result;
    },

    getModelFormat: function (characterId) {
        const character = this.getCharacterById(characterId);
        if (character && character.modelFormat) {
            return character.modelFormat;
        }
        return "fbx";
    },

    // Function to get a random quote for a character by ID
    getRandomQuote: function (characterId) {
        const character = this.getCharacterById(characterId);
        if (character && character.quotes && character.quotes.length > 0) {
            const randomIndex = Math.floor(
                Math.random() * character.quotes.length,
            );
            return {
                quote: character.quotes[randomIndex],
                author: character.name,
            };
        }
        return null;
    },
};
